type PhilosophyCardColor = 'red' | 'green' | 'blue' | 'yellow'

interface PhilosophyCard {
  chip: string
  title: string
  description: string
  color: PhilosophyCardColor
}

interface PhilosophySectionProps {
  title: string
  cards: PhilosophyCard[]
  imageUrl: string
  imageUrlMobile: string
  imageAlt: string
}

const COLOR_MAP: Record<
  PhilosophyCardColor,
  { chipBg: string; chipText: string; titleText: string }
> = {
  red: {
    chipBg: 'bg-qb-red-50',
    chipText: 'text-qb-red-600',
    titleText: 'text-qb-red-600',
  },
  green: {
    chipBg: 'bg-qb-green-50',
    chipText: 'text-qb-green-600',
    titleText: 'text-qb-green-600',
  },
  blue: {
    chipBg: 'bg-qb-blue-50',
    chipText: 'text-qb-primary',
    titleText: 'text-qb-primary',
  },
  yellow: {
    chipBg: 'bg-qb-yellow-50',
    chipText: 'text-qb-yellow-600',
    titleText: 'text-qb-yellow-600',
  },
}

export function PhilosophySection({
  title,
  cards,
  imageUrl,
  imageUrlMobile,
  imageAlt,
}: PhilosophySectionProps) {
  const [titleLead, titleMain] = title.split('||')

  return (
    <section id="philosophy" data-marketing-section="" className="w-full bg-qb-primary relative rounded-t-[32px] lg:rounded-t-[80px] overflow-x-clip">
      {/* Desktop illustration — absolute, bleeds up out of section (Figma 48:5143) */}
      <img
        src={imageUrl}
        alt={imageAlt}
        className="
          hidden lg:block
          absolute pointer-events-none select-none object-contain
          top-[-141px] right-[calc((100vw-1136px)/2-60px)]
          w-[682px] h-auto max-w-none
          z-10
        "
      />

      <div className="mx-auto max-w-[1136px] px-6 lg:px-0 pt-10 lg:pt-[110px] pb-20 lg:pb-30 relative">
        {/* Headline — Figma mobile (194:2603) centers title at top; desktop (48:5143) left-aligns */}
        <div className="flex flex-col gap-4 tracking-[-0.6px] items-center text-center lg:items-start lg:text-left lg:max-w-[548px]">
          <p className="text-[#FFEE00] text-[28px] lg:text-[40px] font-bold leading-[1.4] lg:leading-[1.2] whitespace-pre-line">
            {titleLead}
          </p>
          <p className="text-white text-[28px] lg:text-[40px] font-bold leading-[1.4] lg:leading-[1.2] whitespace-pre-line">
            {titleMain}
          </p>
        </div>

        {/* Mobile illustration — flows below text, overlaps cards via z-index (Figma 194:2491) */}
        <div className="lg:hidden relative z-0 mt-6 flex justify-center">
          <img
            src={imageUrlMobile}
            alt={imageAlt}
            className="pointer-events-none select-none object-contain w-[409px] h-[347px] max-w-none"
          />
        </div>

        {/* 4 cards */}
        <div data-testid="stat-card-grid" className="relative z-20 -mt-[78px] lg:mt-[100px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((card) => {
            const c = COLOR_MAP[card.color]
            return (
              <article
                key={card.chip}
                className="bg-white rounded-[24px] drop-shadow-[0_8px_8px_rgba(0,0,0,0.2)] flex flex-col items-center gap-4 px-4 lg:px-6 pt-4 lg:pt-8 pb-5 lg:pb-10 w-full"
              >
                <span
                  className={`inline-flex items-center justify-center h-10 lg:h-12 min-w-[104px] lg:min-w-[112px] max-w-[160px] px-4 lg:px-6 py-2 rounded-full ${c.chipBg} ${c.chipText} text-[16px] lg:text-[18px] font-medium tracking-[-0.6px] leading-[1.5]`}
                >
                  {card.chip}
                </span>
                <div className="flex flex-col gap-4 lg:gap-6 items-center text-center w-full">
                  <h3
                    className={`font-bold text-[20px] lg:text-[24px] leading-[1.5] lg:leading-[1.4] tracking-[-0.6px] whitespace-pre-line ${c.titleText}`}
                  >
                    {card.title}
                  </h3>
                  <p className="font-medium text-[18px] leading-[1.5] tracking-[-0.6px] text-qb-text-high whitespace-pre-line">
                    {card.description}
                  </p>
                </div>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
